import {
  ConsultantLeadStatus,
  ConsultantLeadStatusCustom,
  useGetPersonalInfoCardDataLazyQuery,
  useUpdateConsultantLeadStatusMutation
} from "@bonliva-crm/graphql";
import {
  DatePicker,
  RadioButtonGroup,
  TextField,
  useSnackbarsPipeline
} from "@bonliva-crm/ui";
import { useEffect, useMemo } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography
} from "@material-ui/core";
import { useLocale, useUserProfile } from "@hooks";
import { Controller, useForm } from "react-hook-form";
import { NextContactDateReason } from "@bonliva-crm/shared";
import { yupResolver } from "@hookform/resolvers/yup";
import { getDefaultValues, FormInput, validationSchema } from "./formData";

type Props = {
  consultantId: string;
  type: "addCallEvent" | "deAssignHBA" | "default";
  open: boolean;
  setOpen: (open: boolean) => void;
  onSubmit?: () => void;
};

export const LeadStatusModal = (props: Props) => {
  const user = useUserProfile();
  const snack = useSnackbarsPipeline();
  const { locale } = useLocale();

  const [personalCardQuery, personalCardData] = useGetPersonalInfoCardDataLazyQuery({
    fetchPolicy: "cache-first",
    variables: {
      id: props.consultantId
    }
  });

  const data = personalCardData.data;

  const [updateConsultantLeadStatusMutation] = useUpdateConsultantLeadStatusMutation({
    onCompleted: () => {
      snack.showSuccess(locale.Saved);
    },
    onError: (error) => {
      console.error(error);
      snack.showError(locale.ErrorOccurred);
    }
  });

  const { control, watch, formState, trigger, handleSubmit, reset } = useForm<FormInput>({
    defaultValues: () => {
      return personalCardQuery().then((result) => {
        const status =
          ConsultantLeadStatusCustom[result.data.consultantById.leadStatus?.status];

        return getDefaultValues(status);
      });
    },
    mode: "onChange",
    resolver: yupResolver(validationSchema)
  });

  const [_leadStatus, _reason] = watch(["leadStatus", "reason"]);

  useEffect(() => {
    if (_leadStatus === ConsultantLeadStatusCustom.RelevantLater) {
      trigger(["nextContactDate", "reasonComment"]);
    }
  }, [_leadStatus, _reason]);

  const onClose = () => {
    props.setOpen(false);
    reset();
  };

  const updateConsultantLeadStatus = (formData: FormInput) => {
    return updateConsultantLeadStatusMutation({
      variables: {
        consultantId: props.consultantId,
        data: {
          leadStatus: formData.leadStatus,
          nextContactDate: formData.nextContactDate,
          reason: formData.reason,
          reasonComment: formData.reasonComment,
          isResponsibleHBA: formData.isResponsibleHBA
        }
      },
      optimisticResponse(vars) {
        return {
          updateConsultantLeadStatus: {
            ...data.consultantById,
            leadStatus: { status: ConsultantLeadStatus[vars.data.leadStatus] },
            ResponsiblePerson:
              ConsultantLeadStatus[vars.data.leadStatus] === ConsultantLeadStatus.Warm &&
              vars.data.isResponsibleHBA
                ? { PersonId: user.id, Name: user.name }
                : data.consultantById.ResponsiblePerson
          }
        };
      }
    })
      .then((result) => {
        props.onSubmit && props.onSubmit();

        const status =
          ConsultantLeadStatusCustom[
            result.data.updateConsultantLeadStatus.leadStatus.status
          ];

        reset(getDefaultValues(status));
      })
      .finally(onClose);
  };

  const subtitle = useMemo(() => {
    switch (props.type) {
      case "default": {
        return "";
      }
      case "addCallEvent": {
        const leadStatus = data?.consultantById.leadStatus?.status;
        if (
          [
            ConsultantLeadStatus.NotContacted,
            ConsultantLeadStatus.ContactedNoAnswer
          ].includes(leadStatus)
        ) {
          return `Vänligen sätt ny lead-status baserat på samtalet du just haft med konsulten.`;
        }
        if (ConsultantLeadStatusCustom[leadStatus]) {
          return (
            <Typography variant="body1">
              Om konsultens Lead-status har ändrats, vänligen ändra denna nedan. OBS att
              “Dålig tajming” bara ska användas om det är konsultens långsiktiga status.
              Dvs om konsultens Lead-status är “Varm” och{" "}
              <i>
                <b>just detta</b>
              </i>{" "}
              samtal var dålig tajming, behåll “Varm” som status.
            </Typography>
          );
        }
        return "";
      }
      case "deAssignHBA": {
        return `
          Du taggar nu av ansvarig HBA från en profil som har status "Lead".
          Då vill vi gärna att du väljer konsultens nuvarande status nedan så att hen blir sökbar för andra kollegor.
        `;
      }
      default: {
        throw new Error("Invalid lead modal status type");
      }
    }
  }, [data?.consultantById.leadStatus?.status, props.type]);

  const reasonOptions = Object.values(NextContactDateReason).map((x) => ({
    label:
      x === NextContactDateReason.Other ? (
        <Controller
          name={"reasonComment"}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={locale.OtherReason}
              error={!!formState.errors.reasonComment}
              disabled={_reason !== NextContactDateReason.Other}
            />
          )}
        />
      ) : (
        locale.nextContactDateReason[x]
      ),
    value: x
  }));

  return (
    <Dialog
      open={props.open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { overflowY: "visible" } }}
    >
      <DialogTitle>{locale.consultantStatusText}</DialogTitle>

      <DialogContent style={{ overflowY: "visible" }}>
        {subtitle !== "" ? <div style={{ marginBottom: "10px" }}>{subtitle}</div> : null}

        <form id="lead-status-form" onSubmit={handleSubmit(updateConsultantLeadStatus)}>
          <Grid container direction={"column"} spacing={3}>
            <Grid item>
              <Controller
                name={"leadStatus"}
                control={control}
                render={({ field }) => (
                  <RadioButtonGroup
                    {...field}
                    label={locale.changeConsultantStatus}
                    options={Object.values(ConsultantLeadStatusCustom).map((status) => ({
                      label: locale.consultantLeadStatus[status],
                      value: status
                    }))}
                  />
                )}
              />
            </Grid>
            {_leadStatus === ConsultantLeadStatusCustom.RelevantLater && (
              <>
                <Grid item>
                  <Controller
                    name={"nextContactDate"}
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        label={locale.NextContact}
                        error={!!formState.errors.nextContactDate}
                      />
                    )}
                  />
                </Grid>

                <Grid item>
                  <Controller
                    name={"reason"}
                    control={control}
                    render={({ field }) => (
                      <RadioButtonGroup
                        {...field}
                        label={locale.NextContactReason}
                        options={reasonOptions}
                      />
                    )}
                  />
                </Grid>
              </>
            )}
            {_leadStatus === ConsultantLeadStatusCustom.Warm &&
            user.id !== data.consultantById.ResponsiblePerson?.PersonId ? (
              <Grid item>
                <Controller
                  name={"isResponsibleHBA"}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      {...field}
                      control={<Checkbox />}
                      label={
                        "Sätt mig som Ansvarig HBA och flytta till kolumn 'Varm' i min pipeline"
                      }
                    />
                  )}
                />
              </Grid>
            ) : null}
          </Grid>
        </form>
      </DialogContent>

      <DialogActions>
        <Button variant="contained" color="default" onClick={onClose}>
          {locale.Cancel}
        </Button>

        <Button
          type={"submit"}
          form={"lead-status-form"}
          variant="contained"
          color="primary"
          disabled={!formState.isValid || formState.isSubmitting}
        >
          {locale.Save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
